/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import DiagramSymbolMindMapIcon from '@atlaskit/icon-lab/core/diagram-symbol-mind-map';
import StickerIcon from '@atlaskit/icon-lab/core/sticker';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type ManifestEditorToolbarActions,
} from '@atlaskit/native-embeds-common';
import { WHITEBOARD_VIEWPORT_ALIGNMENT_FEATURE } from '@atlaskit/native-embeds-common/whiteboard-viewport-alignment';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import { getInitialThemeParam } from '@atlassian/native-embeds-core/theme';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';
import type { QueryParams } from '@atlassian/native-embeds-core/types/experience/types';

import { NativeEmbedChildCommandType } from './common/types/commands/main';

export const WHITEBOARD_EXPERIENCE_MANIFEST_ID: ExperienceManifestId =
	experienceManifestId('whiteboard');

/**
 * Dynamic-config killswitch for sending `disableCanvasScroll` conditionally in
 * legacy full screen. Default `false` = change enabled (param omitted from the
 * legacy full-screen URL so the blown-up board scrolls); set to `true` to roll
 * back to the pre-EPS8-40 behavior (param sent everywhere).
 */
const LEGACY_FS_CANVAS_SCROLL_KILLSWITCH = 'platform_native_embed_legacy_fs_canvas_scroll';

const whiteboardIcons = [WhiteboardIcon, DiagramSymbolMindMapIcon, ShapesIcon, StickerIcon];

const isWhiteboardUrl = (url: string): boolean =>
	// @ts-ignore - TS1503 TypeScript 5.9.2 upgrade
	url.match(/\/wiki\/spaces\/?.*\/whiteboard\/(?<resourceId>\d+)(\?\/)?/) ||
	url.match(
		/\/wiki\/spaces\/?.*\/whiteboard\/(?<resourceId>[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})(\?\/)?/,
	);

/**
 * Experience manifest for a whiteboard embed.
 * Register with getNativeEmbedFacade().registerExperience(WhiteboardExperienceManifest)
 * to obtain a Experience for EmbedFrameUI.
 */
export const WhiteboardExperienceManifest: {
	readonly handleChildCommands: (_experience: Experience, cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly queryParams: () => QueryParams;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		// eslint-disable-next-line @typescript-eslint/ban-types
		readonly editorToolbarHandlers: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly waitForContentReady: true;
	};
} = {
	id: WHITEBOARD_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, cmd: NativeEmbedChildCommand): void => {
		switch (cmd.type) {
			case NativeEmbedChildCommandType.FreezeFrameResponse:
				// TODO store the position and size of the frozen frame
				break;
			case NativeEmbedChildCommandType.UnfreezeFrameResponse:
				// TODO remove the position and size of the frozen frame from storage
				break;
		}
	},
	isUrlMatch: isWhiteboardUrl,
	supportedFeatures: new Set<NativeEmbedFeature>([
		...allNativeEmbedCoreFeatures,
		WHITEBOARD_VIEWPORT_ALIGNMENT_FEATURE,
	]),
	// Thunk so the killswitch is read at URL-build time (after Statsig init), and
	// so the conditional behavior is gated at its source: when rolled back the
	// manifest emits the pre-EPS8-40 plain-string value, which `resolveQueryParams`
	// passes through unchanged at every build site.
	//
	// `disableCanvasScroll` is correct inline (and in shared-iframe full screen,
	// which reuses the inline URL) but makes the blown-up board feel broken in the
	// legacy full-screen iframe, so by default it is omitted from that build site
	// only. `hideCursors` applies everywhere (plain string === appliesTo: 'always').
	queryParams: (): QueryParams => {
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		const rolledBack = FeatureGates.getExperimentValue(
			LEGACY_FS_CANVAS_SCROLL_KILLSWITCH,
			'value',
			false,
		);
		return rolledBack
			? { disableCanvasScroll: 'true', hideCursors: 'true' }
			: {
					disableCanvasScroll: { value: 'true', appliesTo: 'exceptLegacyFullscreen' },
					hideCursors: 'true',
				};
	},
	transformUrl: (url: string, isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		if (!isFullScreen) {
			urlObj.searchParams.set('hideBorder', 'true');
		}
		// this is used to identify the native embed view in the whiteboard app
		urlObj.searchParams.set('nativeEmbedView', 'true');

		const initialTheme = getInitialThemeParam();
		if (initialTheme) {
			urlObj.searchParams.set('themeState', initialTheme);
		}

		return urlObj.toString();
	},
	isActive: (): true => {
		return true;
	},
	uiConfig: {
		editorToolbarActions: () =>
			createEditorToolbarActions({
				items: [
					BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
					BUILTIN_TOOLBAR_KEYS.SUMMARISE,
					BUILTIN_TOOLBAR_KEYS.SEPARATOR,
					BUILTIN_TOOLBAR_KEYS.EMBED,
					BUILTIN_TOOLBAR_KEYS.EDIT,
					...(fg('cc-maui-toolbar-separators-update') ? [] : [BUILTIN_TOOLBAR_KEYS.SEPARATOR]),
					// BUILTIN_TOOLBAR_KEYS.BORDER, // Border option is yet to be implemented.
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
					...(fg('cc-maui-toolbar-separators-update')
						? []
						: [
								BUILTIN_TOOLBAR_KEYS.SEPARATOR,
								BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
								BUILTIN_TOOLBAR_KEYS.SEPARATOR,
							]),
				],
				moreItems: [
					BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
					BUILTIN_TOOLBAR_KEYS.COPY,
					BUILTIN_TOOLBAR_KEYS.DELETE,
				],
			}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={whiteboardIcons}
				testId={props.testId}
			/>
		),
		titleIcon: WhiteboardIcon,
		appearance: 'overlay',
		waitForContentReady: true,
	},
} as const satisfies ExperienceManifest;
